/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { isExternalLink } from 'bundles/common/utils/urlUtils';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

type Props = {
  title: string;
  markdownContent: string;
  ctaText: string;
  ctaLink: string;
};

const styles = {
  financeSection: css`
    padding: var(--cds-spacing-300) 0;
  `,
  padding: css`
    padding: var(--cds-spacing-300) 0;
  `,
  contentTable: (theme: DegreeDescriptionPageTheme) => css`
    white-space: pre-line;

    strong {
      margin-bottom: var(--cds-spacing-50);
      display: inline-block;
      ${theme.typography.h2semibold}
    }

    .rc-markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${theme.typography.fontFamily};
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-950);
        }
      }
    }

    .rc-Markdown.styled ul {
      border: 2px solid var(--cds-color-grey-50);
      border-radius: 8px;
      padding-left: 0;
      margin: 0;
      ${breakpoints.up('md')} {
        display: flex;
      }
    }

    .rc-Markdown.styled ul li {
      list-style: none;
      padding: var(--cds-spacing-400);
      margin-bottom: 0;
      ${breakpoints.up('md')} {
        border-right: 2px solid var(--cds-color-grey-50);
        width: 33%;
      }
      ${breakpoints.down('sm')} {
        border-bottom: 2px solid var(--cds-color-grey-50);
      }
      ${theme.mobileFontSizeStyle?.body};
    }

    .rc-Markdown.styled ul li:last-of-type {
      border-bottom: none;
      border-right: none;
    }

    .rc-Markdown.styled ul li strong {
      ${theme.mobileFontSizeStyle?.subHeader}
    }
  `,
};

const FinanceSectionModule = ({ title, markdownContent, ctaText, ctaLink }: Props) => {
  const { trackClickButton, useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const theme = useDegreeDescriptionPageTheme();
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');
  const pageSectionRef = useGetViewPageSectionRef('finaid_scholarships');

  const handleOnClick = () => {
    trackClickButton('degree_info_link', 'finaid_scholarships', ctaLink, ctaText);
  };

  return (
    <div ref={pageSectionRef}>
      <Container>
        <Grid container css={styles.financeSection}>
          <Grid item xs={12} css={styles.padding}>
            <Typography2 variant="titleSmall" css={theme.mobileFontSizeStyle?.header} component="h2">
              {title}
            </Typography2>
          </Grid>
          <Grid item xs={12} css={styles.contentTable(theme)}>
            <Markdown
              source={markdownContent}
              buttonTrackingNameV3="degree_info_link"
              trackingSection="finaid_scholarships"
              trackingProductId={degreeId}
            />
          </Grid>
          {ctaLink && ctaText && (
            <Grid item css={styles.padding}>
              <Link
                href={ctaLink}
                target={isExternalLink(ctaLink) ? '_blank' : undefined}
                rel={isExternalLink(ctaLink) ? 'noopener noreferrer' : undefined}
                component={TrackedLink2}
                trackingName="finance_section_link"
                variant="quiet"
                typographyVariant="subtitleMedium"
                aria-label={ctaText}
                onClick={handleOnClick}
              >
                {ctaText}
              </Link>
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(FinanceSectionModule);
